import React from 'react';
import { Navigate, Outlet, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { LayoutDashboard, LogOut, Package, Mail, Eye } from 'lucide-react';
import { Logo } from '../../Logo';
const navItems = [{
  to: '/admin',
  label: 'Dashboard',
  icon: LayoutDashboard,
  exact: true
}, {
  to: '/admin/orders',
  label: 'Orders',
  icon: Package,
  exact: false
}, {
  to: '/admin/newsletter',
  label: 'Newsletter',
  icon: Mail,
  exact: false
}];
export function AdminLayout() {
  const {
    user,
    profile,
    loading,
    signOut
  } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Prototyping bypass: `?preview=true` renders the admin shell without a
  // signed-in admin, mirroring the same flag used by the Study experience.
  const isPreview = searchParams.get('preview') === 'true';
  const withPreview = (path: string) => isPreview ? `${path}?preview=true` : path;
  if (!isPreview && loading) {
    return <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center" data-id="element-164" data-mp="20698ws3y4j6n">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" role="status" aria-label="Checking admin access" data-id="element-165" data-mp="2539ive3x2631" />
      </div>;
  }
  if (!isPreview && (!user || profile?.role !== 'admin')) {
    return <Navigate to="/admin/login" state={{
      from: location
    }} replace data-id="element-166" data-mp="2jtp2iogop5z3" />;
  }
  return <div className="min-h-screen bg-[#F9F9F9] flex" data-id="element-167" data-mp="2nspi7y3v7shv">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-[#E5E5E5] flex flex-col" data-id="element-168" data-mp="2sun5r4jat9gm">
        <div className="p-6 border-b border-[#E5E5E5]" data-id="element-169" data-mp="2xsyipo4w4032">
          <Link to={withPreview('/admin')} className="flex items-center gap-3" data-id="element-170" data-mp="2xqm0d9v7znph">
            <Logo className="w-8 h-8" data-id="element-171" data-mp="yhf9d8focpsv" />
            <span className="font-bold text-[#111111] tracking-tight" data-id="element-172" data-mp="13efg7lkclwl1">
              Admin
            </span>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1" aria-label="Admin navigation" data-id="element-173" data-mp="ver9fn4iq3a1">
          {navItems.map(({
          to,
          label,
          icon: Icon,
          exact
        }) => {
          const active = exact ? location.pathname === to : location.pathname.startsWith(to);
          return <Link key={to} to={withPreview(to)} aria-current={active ? 'page' : undefined} className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${active ? 'bg-[#F0F4F8] text-tealslate' : 'text-[#5A5A5A] hover:bg-[#F9F9F9] hover:text-[#111111]'}`} data-id="element-174" data-mp="10c169nu8lh7w">
                <Icon className="w-5 h-5" data-id="element-175" data-mp="1j3u1lows3eis" />
                {label}
              </Link>;
        })}
        </nav>
        <div className="p-4 border-t border-[#E5E5E5]" data-id="element-176" data-mp="1y1w5k07q220k">
          {isPreview ? <Link to="/admin/login" className="flex items-center gap-3 px-4 py-3 w-full rounded-md text-sm font-medium text-[#5A5A5A] hover:bg-[#F9F9F9] hover:text-[#111111] transition-colors" data-id="element-177" data-mp="1t29qdl8c2bp4">
              <LogOut className="w-5 h-5" data-id="element-178" data-mp="2golh5g1loeau" />
              Exit preview
            </Link> : <button onClick={() => signOut()} className="flex items-center gap-3 px-4 py-3 w-full rounded-md text-sm font-medium text-[#5A5A5A] hover:bg-[#F9F9F9] hover:text-[#111111] transition-colors" data-id="element-179" data-mp="22oubc2l8m47x">
              <LogOut className="w-5 h-5" data-id="element-180" data-mp="1xq0v42ekfz42" />
              Sign Out
            </button>}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto" data-id="element-181" data-mp="1k0ff90w2c009">
        {isPreview && <div className="bg-[#FFF7E6] border-b border-[#F0D9A8] px-8 py-3 flex items-center gap-3" data-id="element-182" data-mp="1oz0jrcicl5ui">
            <Eye className="w-4 h-4 text-[#8A6D1F] shrink-0" data-id="element-183" data-mp="1a1pyxcjtenm4" />
            <p className="text-sm text-[#8A6D1F]" data-id="element-184" data-mp="1ezi1d0i35s4k">
              <strong className="font-bold" data-id="element-185" data-mp="1ez9o37i1wksu">Preview mode</strong> — you are not
              signed in. Live data may be unavailable until an admin account
              signs in.
            </p>
          </div>}
        <Outlet data-id="element-186" data-mp="2ddoyihvnn8hj" />
      </main>
    </div>;
}